import { onClickOutside } from '@vueuse/core';
import { addDays } from 'date-fns';
import { ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { toDate, type DateLike } from './datepicker-utils';
import { useDatepickerFloating } from './use-datepicker-floating';

export type RangeEdge = 'start' | 'end';

/**
 * Start/end fields that open one inline range picker, like the website booking bar:
 * the clicked field picks the edge the next day click sets, clicking it again closes the picker.
 * Picks show up in `dates` right away and are committed once the picker closes.
 * Bind `floatingStyles` to the picker container; it opens below the first field and stays on screen.
 */
export function useRangePickerFields(options: {
  startDate: MaybeRefOrGetter<DateLike>;
  endDate: MaybeRefOrGetter<DateLike>;
  disabled?: MaybeRefOrGetter<boolean | undefined>;
  pickerContainer: Readonly<Ref<HTMLElement | null>>;
  fields: Readonly<Ref<HTMLElement | null>>[];
  onCommit: (startDate: Date, endDate: Date) => void;
}) {
  const getCommittedDates = (): [Date | null, Date | null] => [
    toDate(toValue(options.startDate)),
    toDate(toValue(options.endDate)),
  ];

  const dates = ref<[Date | null, Date | null]>(getCommittedDates());
  watch(getCommittedDates, (committedDates) => (dates.value = committedDates));

  const showPicker = ref(false);
  const activeEdge = ref<RangeEdge>('start');

  const closePicker = () => {
    showPicker.value = false;
    const [startDate, endDate] = dates.value;
    if (!startDate) return;
    // A start picked after the old end leaves the end open; default it like the website does.
    const committedEndDate = endDate ?? addDays(startDate, 2);
    const [previousStartDate, previousEndDate] = getCommittedDates();
    if (
      previousStartDate?.getTime() === startDate.getTime() &&
      previousEndDate?.getTime() === committedEndDate.getTime()
    ) {
      return;
    }
    options.onCommit(startDate, committedEndDate);
  };

  const toggleEdge = (edge: RangeEdge) => {
    if (toValue(options.disabled)) return;
    if (showPicker.value && activeEdge.value === edge) {
      closePicker();
      return;
    }
    activeEdge.value = edge;
    showPicker.value = true;
  };

  onClickOutside(options.pickerContainer, () => showPicker.value && closePicker(), {
    ignore: options.fields,
  });

  const floatingStyles = useDatepickerFloating(options.fields[0], options.pickerContainer);

  return { dates, showPicker, activeEdge, toggleEdge, floatingStyles };
}
